import { useCallback } from 'react';
import { doc, deleteDoc, setDoc, updateDoc, UpdateData, DocumentData } from 'firebase/firestore';
import { db } from '../../../firebase';
import { Task, FullProject, LogEntry, ProjectMetadata, StakeholderEntry } from '../../../types';
import { globalCommandManager } from '../../../utils/commandManager';
import { createDeleteTaskCommand } from '../../../utils/taskCommands';
import { undoToastManager } from '../../../utils/undoToastManager';
import {
  areProjectIdsMatching,
  isMatchingTaskId,
} from '../../../utils/idNormalizer';
import { normalizePersonName } from '../../../components/communication/personModel';
import { executePoolContactDeletion } from './poolContactDeleteEngine';
import { ProjectSyncTracker } from '../sync/projectSyncStateTracker';

export interface UseTaskDeleteActionsProps {
  syncTracker: ProjectSyncTracker;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  currentProjectId: string;
  tasks: Task[];
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  setProjectMetadata: React.Dispatch<React.SetStateAction<ProjectMetadata>>;
  addLogEntry: (action: LogEntry['action'], description: string, details?: string) => void;
  markProjectDirty: (id: string) => void;
  onMarkTaskDirty: (taskId: string | number, projectId?: string) => void;
  updateCurrentProjectTasks: (currentProj: FullProject, updatedProjList: FullProject[]) => void;
  COMM_PROJECT_ID: string;
}

export function useTaskDeleteActions({
  syncTracker,
  setAllProjects,
  currentProjectId,
  tasks,
  setTasks,
  setProjectMetadata,
  addLogEntry,
  markProjectDirty,
  onMarkTaskDirty,
  updateCurrentProjectTasks,
  COMM_PROJECT_ID,
}: UseTaskDeleteActionsProps) {
  const deleteTask = useCallback((id: number | string) => {
    if (!id) return;
    // Transactional undo/redo is handled via globalCommandManager (deleteCmd / poolDeleteCmd)
    // to prevent dual undo stack pollution with full project snapshots.

    let deletedTaskFound = false;
    let deletedTaskName = "";
    let modifiedProjectId = "";
    let realTaskIdStr = "";
    let deletedTaskObj: Task | undefined;
    let deletedSubtasksList: Task[] = [];
    const taskIdStr = String(id).trim();
    if (!taskIdStr) return;

    // 0. Check if this is a pool contact (synthetic ID e.g. pool_contact_<projId>_<name>)
    if (
      taskIdStr.includes('pool_contact_') &&
      executePoolContactDeletion({
        taskIdStr,
        syncTracker,
        setAllProjects,
        currentProjectId,
        setProjectMetadata,
        addLogEntry,
        markProjectDirty,
        updateCurrentProjectTasks,
        COMM_PROJECT_ID,
        onRedoDelete: deleteTask,
      })
    ) {
      return;
    }

    const unassignedTasksSet = new Set<string>();
    const unassignedTasksDirectUpdates: Array<{ id: string; updates: UpdateData<DocumentData> }> = [];
    let isContactDeletion = false;
    let contactDeletionName = '';

    let updatedProjects = syncTracker.getAllProjects().map(p => {
      const found = p.tasks.find(t => isMatchingTaskId(taskIdStr, t.id, p.id));
      if (found) {
        deletedTaskFound = true;
        modifiedProjectId = p.id;
        deletedTaskName = found.name;
        realTaskIdStr = String(found.id).trim();
        deletedTaskObj = { ...found };
        
        const isContactTask = found.type === 'contact' || found.status === 'pool';
        const contactPersonName = isContactTask ? (found.assignee || found.name || '').trim() : '';
        const lowerContactName = contactPersonName.toLowerCase();
        const normContactName = normalizePersonName(contactPersonName);
        if (isContactTask && contactPersonName) {
          isContactDeletion = true;
          contactDeletionName = contactPersonName;
        }

        const childTasks = p.tasks.filter(t => String(t.parentId).trim() === realTaskIdStr);
        const childrenCount = childTasks.length;
        deletedSubtasksList = childTasks.map(c => ({ ...c }));
        
        const newTasks = p.tasks
          .filter(t => String(t.id).trim() !== realTaskIdStr)
          .map(t => {
            let taskObj = t;
            if (String(taskObj.parentId).trim() === realTaskIdStr) {
              taskObj = { ...taskObj, parentId: undefined };
            }
            if (isContactTask && (lowerContactName || normContactName) && taskObj.assignee) {
              const assignees = taskObj.assignee.split(/[,;/]/).map(a => a.trim()).filter(Boolean);
              const matches = (a: string) => a.toLowerCase() === lowerContactName || Boolean(normContactName && normalizePersonName(a) === normContactName);
              if (assignees.some(matches)) {
                unassignedTasksSet.add(String(taskObj.id));
                const remaining = assignees.filter(a => !matches(a)).join(', ');
                const now = Date.now();
                taskObj = { ...taskObj, assignee: remaining, updatedAt: now };
                unassignedTasksDirectUpdates.push({ id: String(taskObj.id), updates: { assignee: remaining, updatedAt: now } });
              }
            }
            if (isContactTask && String(taskObj.contactId) === realTaskIdStr) {
              unassignedTasksSet.add(String(taskObj.id));
              const now = Date.now();
              taskObj = { ...taskObj, contactId: '', updatedAt: now };
              unassignedTasksDirectUpdates.push({ id: String(taskObj.id), updates: { contactId: '', updatedAt: now } });
            }
            return taskObj;
          });
        
        const newMetadata = { ...(p.metadata || {}) };
        if (newMetadata.itemOrder) {
          const order = { ...newMetadata.itemOrder };
          Object.keys(order).forEach(key => {
            order[key] = (order[key] || []).filter(oid => String(oid).trim() !== taskIdStr);
          });
          newMetadata.itemOrder = order;
        }

        return { ...p, tasks: newTasks, metadata: newMetadata, lastModified: Date.now() };
      }
      return p;
    });

    if (!deletedTaskFound) {
      const foundInRaw = syncTracker.getRawTasks().find(t => isMatchingTaskId(taskIdStr, t.id, t.projectId));
      const foundInState = tasks.find(t => isMatchingTaskId(taskIdStr, t.id, t.projectId || currentProjectId));
      const fallbackTask = foundInRaw || foundInState;
      if (fallbackTask) {
        deletedTaskFound = true;
        deletedTaskName = fallbackTask.name;
        realTaskIdStr = String(fallbackTask.id).trim();
        deletedTaskObj = { ...fallbackTask };
        modifiedProjectId = fallbackTask.projectId || currentProjectId;

        updatedProjects = updatedProjects.map(p => ({
          ...p,
          lastModified: Date.now(),
          tasks: (p.tasks || []).filter(t => !isMatchingTaskId(t.id, realTaskIdStr))
        }));
      }
    }

    if (deletedTaskFound) {
      syncTracker.recordRecentlyDeleted([realTaskIdStr, taskIdStr]);

      if (isContactDeletion && contactDeletionName) {
        const lowerContactName = contactDeletionName.trim().toLowerCase();
        const normContactName = normalizePersonName(contactDeletionName);
        const matches = (a?: string | null) => {
          if (!a) return false;
          return a.trim().toLowerCase() === lowerContactName || Boolean(normContactName && normalizePersonName(a) === normContactName);
        };
        updatedProjects = updatedProjects.map(p => {
          let pChanged = false;
          let pTasks = p.tasks || [];
          let pMetadata = p.metadata;

          const hasAssigneeMatch = pTasks.some(t =>
            (t.assignee && t.assignee.split(/[,;/]/).some(a => matches(a))) ||
            String(t.contactId) === realTaskIdStr
          );
          if (hasAssigneeMatch) {
            pChanged = true;
            pTasks = pTasks.map(t => {
              let nextT = t;
              let tChanged = false;
              const updates: UpdateData<DocumentData> = {};
              if (t.assignee) {
                const assignees = t.assignee.split(/[,;/]/).map(a => a.trim()).filter(Boolean);
                if (assignees.some(a => matches(a))) {
                  const remaining = assignees.filter(a => !matches(a)).join(', ');
                  nextT = { ...nextT, assignee: remaining };
                  updates.assignee = remaining;
                  tChanged = true;
                }
              }
              if (String(t.contactId) === realTaskIdStr) {
                nextT = { ...nextT, contactId: '' };
                updates.contactId = '';
                tChanged = true;
              }
              if (tChanged) {
                const now = Date.now();
                nextT = { ...nextT, updatedAt: now };
                updates.updatedAt = now;
                unassignedTasksSet.add(String(t.id));
                unassignedTasksDirectUpdates.push({ id: String(t.id), updates });
              }
              return nextT;
            });
          }

          if (pMetadata?.stakeholders) {
            const prevLen = pMetadata.stakeholders.length;
            const nextStakeholders = pMetadata.stakeholders.filter((s: StakeholderEntry) => !matches(s?.name));
            if (nextStakeholders.length !== prevLen) {
              pChanged = true;
              pMetadata = { ...pMetadata, stakeholders: nextStakeholders };
            }
          }

          if (pChanged) {
            markProjectDirty(p.id);
            return { ...p, tasks: pTasks, metadata: pMetadata, lastModified: Date.now() };
          }
          return p;
        });

        markProjectDirty(COMM_PROJECT_ID);
        const synthId = `pool_contact_${modifiedProjectId || currentProjectId}_${encodeURIComponent(contactDeletionName)}`;
        syncTracker.recordRecentlyDeleted([synthId]);
      }

      syncTracker.recordDirtyTasks(unassignedTasksSet);

      const lowerContactNameForRaw = contactDeletionName.trim().toLowerCase();
      const normContactNameForRaw = normalizePersonName(contactDeletionName);
      const matchesRaw = (a?: string | null) => {
        if (!a || !isContactDeletion) return false;
        return a.trim().toLowerCase() === lowerContactNameForRaw || Boolean(normContactNameForRaw && normalizePersonName(a) === normContactNameForRaw);
      };

      const cleanRawList = (list: Task[]) =>
        list
          .filter(t => !isMatchingTaskId(t.id, realTaskIdStr) && !isMatchingTaskId(t.id, taskIdStr))
          .map(t => {
            if (!isContactDeletion) return t;
            let nextT = t;
            if (t.assignee) {
              const assignees = t.assignee.split(/[,;/]/).map(a => a.trim()).filter(Boolean);
              if (assignees.some(a => matchesRaw(a))) {
                nextT = { ...nextT, assignee: assignees.filter(a => !matchesRaw(a)).join(', '), updatedAt: Date.now() };
              }
            }
            if (String(t.contactId) === realTaskIdStr) {
              nextT = { ...nextT, contactId: '', updatedAt: Date.now() };
            }
            return nextT;
          });

      syncTracker.setRawAndOwnedTasks(
        cleanRawList(syncTracker.getRawTasks()),
        cleanRawList(syncTracker.getOwnedTasks())
      );

      if (deletedTaskName && deletedTaskName.trim()) {
        addLogEntry('delete', `Aufgabe "${deletedTaskName}" gelöscht`);
      }
      syncTracker.commitProjects(updatedProjects, setAllProjects);
      
      const currentInNew = updatedProjects.find(p => areProjectIdsMatching(p.id, currentProjectId));
      if (currentInNew) {
        updateCurrentProjectTasks(currentInNew, updatedProjects);
        setProjectMetadata(currentInNew.metadata || {});
      }

      markProjectDirty(modifiedProjectId || currentProjectId); 

      if (realTaskIdStr && !realTaskIdStr.includes('pool_contact_')) {
        const taskDocRef = doc(db, 'tasks', realTaskIdStr);
        deleteDoc(taskDocRef).catch(err => {
          const errCode = (err?.code || '').toLowerCase();
          const errMsg = (err?.message || '').toLowerCase();
          if (errCode === 'not-found' || errMsg.includes('not found') || errMsg.includes('missing or insufficient permissions')) {
            console.debug(`[ProjectManager] Direct task deletion completed or skipped for unsynced task ${realTaskIdStr}`);
          } else {
            console.error(`[ProjectManager] Failed to delete task ${realTaskIdStr} directly from Firestore:`, err);
          }
        });
      }

      unassignedTasksDirectUpdates.forEach(({ id: uId, updates }) => {
        if (uId && !uId.includes('pool_contact_')) {
          const taskDocRef = doc(db, 'tasks', uId);
          updateDoc(taskDocRef, updates).catch(() => {});
        }
      });

      if (deletedTaskObj) {
        const targetPId = modifiedProjectId || currentProjectId;
        const taskBackup = { ...deletedTaskObj };
        const subtasksBackup = [...deletedSubtasksList];
        const synthContactId =
          isContactDeletion && contactDeletionName
            ? `pool_contact_${targetPId}_${encodeURIComponent(contactDeletionName)}`
            : '';

        const deleteCmd = createDeleteTaskCommand({
          task: taskBackup,
          subtasks: subtasksBackup,
          projectId: targetPId,
          onRestore: async (restoredTask, restoredSubtasks, pId) => {
            const finalPId = pId || targetPId;
            const now = Date.now();
            const stampedTask: Task = { ...restoredTask, updatedAt: now };
            const stampedSubtasks: Task[] = (restoredSubtasks || []).map((s) => ({
              ...s,
              parentId: stampedTask.id,
              updatedAt: now,
            }));

            syncTracker.clearRecentlyDeleted([
              stampedTask.id,
              realTaskIdStr,
              taskIdStr,
              ...(synthContactId ? [synthContactId] : []),
            ]);
            syncTracker.recordDirtyTasks([
              stampedTask.id,
              ...stampedSubtasks.map((s) => s.id),
            ]);

            const nextProjects = syncTracker.getAllProjects().map((proj) => {
              if (areProjectIdsMatching(proj.id, finalPId)) {
                const currentTasks = proj.tasks || [];
                if (currentTasks.some((t) => String(t.id) === String(stampedTask.id))) return proj;

                const restoredSubIds = new Set(stampedSubtasks.map((s) => String(s.id)));
                const updatedTasks = currentTasks.map((t) => {
                  if (restoredSubIds.has(String(t.id))) {
                    return { ...t, parentId: stampedTask.id, updatedAt: now };
                  }
                  return t;
                });

                const newTaskList = [...updatedTasks, stampedTask].sort(
                  (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
                );
                return { ...proj, tasks: newTaskList, lastModified: now };
              }
              return proj;
            });

            syncTracker.syncMultipleTasks([stampedTask, ...stampedSubtasks], true);
            syncTracker.commitProjects(nextProjects, setAllProjects, [finalPId]);

            if (areProjectIdsMatching(finalPId, currentProjectId)) {
              setTasks((prev) => {
                if (prev.some((t) => String(t.id) === String(stampedTask.id))) return prev;
                return [...prev, stampedTask].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
              });
            }

            markProjectDirty(finalPId);
            onMarkTaskDirty(stampedTask.id, finalPId);

            try {
              const restoredDocRef = doc(db, 'tasks', String(stampedTask.id));
              await setDoc(restoredDocRef, stampedTask, { merge: true });
            } catch (restoreErr) {
              console.warn('[Undo] Failed to restore task to Firestore directly:', restoreErr);
            }

            window.dispatchEvent(
              new CustomEvent('gwantt-task-restored', {
                detail: {
                  taskId: stampedTask.id,
                  name: stampedTask.name,
                  assignee: stampedTask.assignee,
                },
              })
            );
            addLogEntry('create', `Aufgabe "${stampedTask.name}" wiederhergestellt`);
          },
          onDelete: async (taskId) => {
            deleteTask(taskId);
          },
        });

        const wasRedoing = globalCommandManager.isCurrentlyRedoing;
        globalCommandManager.registerExecuted(deleteCmd);

        const hasValidName = deletedTaskName && deletedTaskName.trim().length > 0;
        if (hasValidName && !wasRedoing) {
          undoToastManager.show({
            message: `Aufgabe „${deletedTaskName.trim()}“ gelöscht`,
            actionLabel: 'Rückgängig',
            icon: 'delete',
            onUndo: async () => {
              await globalCommandManager.undo();
            },
          });
        }
      }
    } else {
      console.warn(`[ProjectManager] deleteTask: Task with ID ${id} not found in any project.`);
    }
  }, [currentProjectId, addLogEntry, setAllProjects, markProjectDirty, syncTracker, tasks, onMarkTaskDirty, setTasks, setProjectMetadata, updateCurrentProjectTasks, COMM_PROJECT_ID]);

  return {
    deleteTask,
  };
}
